/**
 * CAPTURE PRÉCOCE de `beforeinstallprompt` (store module-level).
 *
 * ── POURQUOI UN STORE, ET PAS UN `useEffect` ──────────────────────────────────────
 *
 * Chromium émet `beforeinstallprompt` TRÈS TÔT dans le chargement de la page — souvent
 * avant que React n'ait monté quoi que ce soit. L'événement ne se rejoue JAMAIS : un
 * écouteur posé dans un effet arrive parfois après lui, et l'affordance « Installer
 * l'application » reste alors absente pour TOUTE la session, sans que rien ne le signale.
 *
 * C'est ce décalage — et non un aléa de test — qui rendait `AccountSwitcher.install`
 * intermittent. Instrumenté sur un échec reproduit, le hook affichait `mounts: 1`,
 * `unmounts: 0`, `installedInit: false` et surtout **`events: 0`** : le composant était
 * bien monté une seule fois, mais l'événement avait été émis dans la fenêtre entre le
 * commit du DOM (que `findByRole` observe) et le vidage des effets passifs (où
 * `addEventListener` était posé). Sous charge, cette fenêtre s'élargit. Le test ne
 * faisait que révéler, une fois sur quatre, ce qui se produit aussi dans un vrai
 * navigateur — mais silencieusement.
 *
 * L'écouteur est donc posé à l'IMPORT du module, avant tout rendu, et l'événement
 * différé est CONSERVÉ jusqu'à ce qu'un composant le réclame. Même patron que les autres
 * stores module-level du projet (`lib/activeSlot`, `notifications/job-activity`), lu par
 * `useSyncExternalStore`.
 */

/**
 * Événement `beforeinstallprompt` (proposition d'installation PWA).
 *
 * Non standardisé (Chromium seulement), donc absent des types du DOM : on le déclare
 * localement au minimum utile (`prompt()` + `userChoice`) plutôt que de recourir à `any`
 * (règle projet : zéro `any`).
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** Ce que le store expose : l'événement en attente, et le fait d'avoir été installé. */
export interface InstallPromptSnapshot {
  /** Événement différé prêt à être rejoué sur geste utilisateur, ou `null`. */
  deferred: BeforeInstallPromptEvent | null;
  /** `appinstalled` reçu — l'application vient d'être installée. */
  installed: boolean;
}

/**
 * Instantané VIDE partagé. `useSyncExternalStore` compare les instantanés par IDENTITÉ :
 * en renvoyer un neuf à chaque lecture provoquerait une boucle de rendu infinie.
 */
const EMPTY: InstallPromptSnapshot = { deferred: null, installed: false };

let snapshot: InstallPromptSnapshot = EMPTY;
const subscribers = new Set<() => void>();

/** Publie un nouvel instantané et notifie les abonnés. */
function publish(next: InstallPromptSnapshot): void {
  snapshot = next;
  for (const cb of subscribers) cb();
}

/** Instantané courant (référence stable tant que rien ne change). */
export function getInstallPromptSnapshot(): InstallPromptSnapshot {
  return snapshot;
}

/** Abonnement aux changements (retourne la fonction de désabonnement). */
export function subscribeInstallPrompt(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Consomme l'événement différé, après l'avoir rejoué : le navigateur ne permet de
 * l'utiliser QU'UNE FOIS, le garder promettrait une installation impossible.
 */
export function clearInstallPrompt(): void {
  if (snapshot.deferred !== null) publish({ ...snapshot, deferred: null });
}

/**
 * Remise à zéro — RÉSERVÉE AUX TESTS. L'état d'un store module-level survit d'un test à
 * l'autre dans un même fichier ; sans ce point de reprise, un test qui émet l'événement
 * rendrait « installable » tous ses suivants (même besoin que `resetViewTracker`).
 */
export function resetInstallPromptStore(): void {
  publish(EMPTY);
}

// Écouteurs posés À L'IMPORT (cf. l'en-tête), jamais retirés : ils vivent aussi longtemps
// que l'application. Gardés pour un environnement sans `window` (SSR, tests node).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Empêche la mini-infobar native → c'est nous qui choisissons le moment de proposer.
    e.preventDefault();
    publish({ deferred: e as BeforeInstallPromptEvent, installed: false });
  });
  window.addEventListener('appinstalled', () => {
    publish({ deferred: null, installed: true });
  });
}
